import type { SharedMarketingProps } from '@/types/marketing'

interface PolicySection {
  title: string
  content: string
  bullets: string[]
  extra: string | null
}

interface ReapplyPolicyProps extends SharedMarketingProps {
  last_updated: string
  sections: PolicySection[]
}

function Section({ section }: { section: PolicySection }) {
  return (
    <section className="space-y-4">
      <h2 className="text-xl font-semibold text-white">{section.title}</h2>
      <p className="text-sm leading-7 text-white/72">{section.content}</p>
      {section.bullets.length > 0 && (
        <ul className="list-disc space-y-1.5 pl-6 text-sm leading-7 text-white/72">
          {section.bullets.map((bullet) => (
            <li key={bullet}>{bullet}</li>
          ))}
        </ul>
      )}
      {section.extra && (
        <p className="text-sm leading-7 text-white/72">{section.extra}</p>
      )}
    </section>
  )
}

export default function ReapplyPolicy(props: ReapplyPolicyProps) {
  return (
    <div className="container mx-auto px-6 pb-20 pt-32 lg:pt-40">
      <div className="mx-auto max-w-3xl">
        <header className="mb-12">
          <h1 className="marketing-display text-3xl lg:text-4xl">Reapplication Policy</h1>
          <p className="mt-4 text-sm text-white/50">
            Last updated: {props.last_updated}
          </p>
          <p className="mt-6 text-sm leading-7 text-white/72">
            If a previous application or account was rejected or terminated for copyright violations,
            fraudulent activity, or platform abuse, you should not submit a new application. This
            page explains why — and what the consequences of trying anyway are. By submitting an
            application you confirm you have read this.
          </p>
        </header>

        <div className="space-y-10">
          {props.sections.map((section) => (
            <Section key={section.title} section={section} />
          ))}
        </div>

        <footer className="mt-14 rounded-lg border border-white/10 bg-white/[0.03] p-6">
          <h2 className="text-lg font-semibold text-white">Think you qualify for an exception?</h2>
          <p className="mt-2 text-sm leading-7 text-white/72">
            Write to <a href="mailto:help@wavbee.com" className="text-[#ff6a1a] hover:underline">help@wavbee.com</a> with
            your original application reference and any new documentation before resubmitting.
            Submitting blindly will burn another verification charge that will not be refunded.
          </p>
        </footer>
      </div>
    </div>
  )
}
